<template>
  <!-- Khung giờ coach nhận nhắc tin chờ duyệt (cron DecisionAiCoachWarning). Chưa tô ô nào = nhắc mọi lúc -->
  <b-modal
    :visible="visible"
    title="Thời gian hoạt động của coach"
    size="xl"
    centered
    hide-footer
    @change="$emit('change', $event)"
    @show="load"
  >
    <div class="cah">
      <aside class="cah-coaches">
        <b-form-input v-model="search" size="sm" placeholder="Tìm coach..." class="mb-2" />
        <div v-if="loading" class="cah-empty"><b-spinner small /> Đang tải…</div>
        <div v-else-if="!filteredCoaches.length" class="cah-empty">Không có coach</div>
        <b-list-group v-else class="cah-coach-list">
          <b-list-group-item
            v-for="c in filteredCoaches"
            :key="c.id"
            button
            :active="c.id === selectedId"
            class="cah-coach"
            @click="selectCoach(c.id)"
          >
            <b-avatar :text="initials(c.fullName)" size="26px" class="cah-coach__avatar" />
            <span class="cah-coach__name">{{ c.fullName }}</span>
            <span v-if="hasHours(c.id)" class="cah-coach__dot" title="Đã thiết lập khung giờ"></span>
          </b-list-group-item>
        </b-list-group>
      </aside>

      <section v-if="selectedCoach" class="cah-main">
        <div class="cah-main__head">
          <strong>{{ selectedCoach.fullName }}</strong>
          <b-badge pill :variant="gridHasHours ? 'success' : 'light'">{{ gridHasHours ? 'Đã thiết lập' : 'Nhắc mọi lúc' }}</b-badge>
        </div>
        <p class="cah-desc">
          Chọn khoảng thời gian hoạt động của bot nhắc nhở, nếu ngoài khung giờ này thì sẽ không nhắc các hoạt động trễ.
        </p>

        <div class="cah-grid-wrap" @mouseleave="hover = null">
          <table class="cah-grid" :class="{ dragging: !!drag, locked: !editing }">
            <thead>
              <tr>
                <th class="cah-grid__corner"></th>
                <th
                  v-for="h in 24"
                  :key="h"
                  colspan="2"
                  class="cah-grid__hour"
                  :title="`Tô / xoá ${h - 1}h cả tuần`"
                  @click="toggleHour(h - 1)"
                >
                  {{ (h - 1) % 2 === 0 ? `${h - 1}h` : '' }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, d) in grid" :key="d">
                <th class="cah-grid__day" :title="`Tô / xoá cả ${dayLabels[d]}`" @click="toggleDay(d)">{{ dayLabels[d] }}</th>
                <td
                  v-for="(on, s) in row"
                  :key="s"
                  class="cah-grid__cell"
                  :class="{ on: cellState(d, s), half: s % 2 === 1 }"
                  :title="`${dayLabels[d]} ${slotLabel(s)}–${slotLabel(s + 1)}`"
                  @mousedown.prevent="startDrag(d, s)"
                  @mouseenter="moveDrag(d, s)"
                ></td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="cah-bottom">
        <!-- Tóm tắt khung giờ đã chọn theo tuần, cập nhật ngay khi kéo -->
        <div class="cah-summary">
          <div class="cah-summary__title">Thời gian đã chọn</div>
          <div v-if="!gridHasHours" class="cah-summary__empty">Chưa chọn khung giờ, bot nhắc mọi lúc.</div>
          <template v-else>
            <div v-for="row in weekSummary" :key="row.day" class="cah-summary__row">
              <span class="cah-summary__day">{{ row.day }}</span>
              <span v-if="row.ranges.length" class="cah-summary__ranges">
                <b-badge v-for="r in row.ranges" :key="r" variant="light" class="cah-summary__range">{{ r }}</b-badge>
              </span>
              <span v-else class="cah-summary__off">Không nhắc</span>
            </div>
          </template>
        </div>

        <!-- Hiệu suất phản hồi: lọc theo nhóm tin + số ngày -->
        <div class="cah-summary cah-stats">
          <div class="cah-summary__title cah-stats__head">
            <span class="cah-stats__title">
              Hiệu suất phản hồi
              <span
                v-b-tooltip.hover.top
                class="cah-stats__info"
                title="Chỉ thống kê tin có gợi ý từ AI, tính từ lúc AI gợi ý tới lúc coach gửi, từ chối hoặc bỏ qua."
              >
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="16" x2="12" y2="12"></line>
                  <line x1="12" y1="8" x2="12.01" y2="8"></line>
                </svg>
              </span>
            </span>
            <b-dropdown size="sm" variant="outline-secondary" right :text="statsCustom ? 'Tuỳ chọn' : `${statsDays} ngày`" class="cah-stats__days">
              <b-dropdown-item
                v-for="n in [7, 14, 30, 90]"
                :key="n"
                :active="!statsCustom && statsDays === n"
                @click="statsCustom = false; statsDays = n; loadStats(selectedId)"
              >
                {{ n }} ngày
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item :active="statsCustom" @click="statsCustom = true; loadStats(selectedId)">Tuỳ chọn ngày…</b-dropdown-item>
            </b-dropdown>
          </div>
          <!-- 1 ngày: bấm cùng 1 ngày 2 lần; khoảng: bấm ngày đầu rồi ngày cuối -->
          <date-picker
            v-if="statsCustom"
            v-model="statsRange"
            range
            type="date"
            format="DD/MM/YYYY"
            value-type="YYYY-MM-DD"
            range-separator=" – "
            placeholder="Chọn 1 ngày hoặc khoảng ngày"
            input-class="form-control form-control-sm"
            class="cah-stats__range"
            :disabled-date="d => d > new Date()"
            @change="loadStats(selectedId)"
          />
          <!-- Tạm chỉ thống kê tin AI gợi ý (statsTypes = ['ai']); mở lại khi cần lọc thêm AI không gợi ý
          <b-form-checkbox-group
            v-model="statsTypes"
            :options="statsTypeOptions"
            size="sm"
            class="cah-stats__types"
            @change="$nextTick(() => loadStats(selectedId))"
          />
          -->
          <div v-if="!statsTypes.length" class="cah-summary__empty">Chọn ít nhất 1 loại tin.</div>
          <div v-else-if="!statsRangeParam" class="cah-summary__empty">Chọn ngày để xem thống kê.</div>
          <div v-else-if="statsLoading" class="cah-summary__empty"><b-spinner small /> Đang tải…</div>
          <div v-else-if="!stats || (!stats.handled && !stats.pending)" class="cah-summary__empty">Chưa có tin nào trong {{ statsPeriodLabel }}.</div>
          <template v-else>
            <div class="cah-stats__tiles">
              <div class="cah-stats__tile">
                <b>{{ stats.handled }}</b>
                <span>tin đã xử lý</span>
              </div>
              <!-- Tạm ẩn trung vị: BE vẫn trả medianMinutes, cần thì mở lại
              <div class="cah-stats__tile">
                <b>{{ formatMinutes(stats.medianMinutes) }}</b>
                <span>trung vị</span>
              </div>
              -->
              <div class="cah-stats__tile">
                <b>{{ stats.within15Percent != null ? `${stats.within15Percent}%` : '—' }}</b>
                <span>≤ 15 phút</span>
              </div>
              <div class="cah-stats__tile">
                <b>{{ stats.edited }}</b>
                <span>tin chỉnh sửa</span>
              </div>
            </div>
            <div v-for="b in stats.buckets" :key="b.label" class="cah-stats__bucket">
              <span class="cah-stats__label">{{ b.label }}</span>
              <span class="cah-stats__track">
                <span class="cah-stats__bar" :style="{ width: `${bucketMax ? (b.count / bucketMax) * 100 : 0}%` }"></span>
              </span>
              <span class="cah-stats__count">{{ b.count }}</span>
            </div>
          </template>
        </div>
        </div>

        <!-- Lấy lịch coach khác đổ vào lưới để chỉnh tiếp; chỉ ghi khi bấm Lưu -->
        <b-form-group v-if="editing && showReference" label="Tham khảo thời gian của coach khác" label-size="sm" class="cah-ref mt-3 mb-0">
          <b-input-group size="sm">
            <b-form-select v-model="referenceId" :options="referenceCoaches" value-field="id" text-field="fullName">
              <template #first>
                <b-form-select-option :value="null">Chọn coach</b-form-select-option>
              </template>
            </b-form-select>
            <b-input-group-append>
              <b-button variant="outline-primary" :disabled="!referenceId" @click="applyReference">Áp dụng</b-button>
              <b-button variant="outline-secondary" title="Đóng" @click="showReference = false; referenceId = null">✕</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>

        <!-- Đã có khung giờ thì khoá lưới, bấm Chỉnh sửa mới kéo thả được -->
        <div class="cah-actions">
          <b-button v-if="!editing" variant="primary" @click="editing = true">Chỉnh sửa lịch</b-button>
          <template v-else>
            <b-dropdown v-if="referenceCoaches.length" variant="outline-secondary" text="Tuỳ chọn" right class="mr-2">
              <b-dropdown-item @click="showReference = true">Tham khảo thời gian của coach khác…</b-dropdown-item>
            </b-dropdown>
            <b-button variant="outline-danger" class="mr-2" :disabled="saving || !gridHasHours" @click="resetGrid">Đặt lại</b-button>
            <b-button variant="outline-secondary" :disabled="saving" @click="cancelEdit">Huỷ</b-button>
            <b-button variant="success" class="ml-2" :disabled="saving || !dirty" @click="save">
              <b-spinner v-if="saving" small class="mr-1" />
              Lưu
            </b-button>
          </template>
        </div>
      </section>
    </div>
  </b-modal>
</template>

<script>
import DatePicker from 'vue2-datepicker';
import 'vue2-datepicker/index.css';
import decisionAiCoachService from '../services/decisionAiCoachService';

const SLOTS = 48;
const DAYS = 7;
const ONBOARDING_ID = '350a3050-c0f7-11ef-b57a-03ea338ae610';
const emptyGrid = () => Array.from({ length: DAYS }, () => Array(SLOTS).fill(false));
const toStrings = grid => grid.map(row => row.map(on => (on ? '1' : '0')).join(''));
const fromStrings = days =>
  Array.isArray(days) && days.length === DAYS
    ? days.map(s => Array.from({ length: SLOTS }, (_, i) => (s || '')[i] === '1'))
    : emptyGrid();

export default {
  name: 'CoachActiveHoursModal',
  components: { DatePicker },
  model: { prop: 'visible', event: 'change' },
  props: {
    visible: { type: Boolean, default: false },
    // Quản lý thấy mọi coach, coach thường chỉ thấy mình
    canManageAll: { type: Boolean, default: false },
    currentUser: { type: Object, default: null },
  },
  data() {
    return {
      loading: false,
      saving: false,
      search: '',
      coaches: [],
      hoursMap: {},
      selectedId: null,
      grid: emptyGrid(),
      savedSnapshot: '',
      drag: null,
      hover: null,
      referenceId: null,
      // Mở từ nút Tuỳ chọn, mặc định ẩn cho gọn
      showReference: false,
      stats: null,
      // Coach chưa có khung giờ thì mở sẵn để tô, đã có thì khoá tới khi bấm Chỉnh sửa
      editing: false,
      statsDays: 30,
      // Tuỳ chọn: statsRange = ['YYYY-MM-DD', 'YYYY-MM-DD'] giờ VN, 2 đầu tính trọn ngày
      statsCustom: false,
      statsRange: [],
      statsTypes: ['ai'],
      statsTypeOptions: [
        { value: 'ai', text: 'AI gợi ý' },
        { value: 'noai', text: 'AI không gợi ý' },
      ],
      statsLoading: false,
      dayLabels: ['T2', 'T3', 'T4', 'T5', 'T6', 'T7', 'CN'],
    };
  },
  computed: {
    filteredCoaches() {
      const q = this.search.trim().toLowerCase();
      return q ? this.coaches.filter(c => (c.fullName || '').toLowerCase().includes(q)) : this.coaches;
    },
    selectedCoach() {
      return this.coaches.find(c => c.id === this.selectedId) || null;
    },
    // Coach khác đã có khung giờ để tham khảo
    // Mỗi ngày: các khoảng liên tục đã tô, vd "09:30 – 12:00"
    weekSummary() {
      return this.grid.map((row, d) => {
        const ranges = [];
        let start = null;
        for (let s = 0; s <= SLOTS; s++) {
          const on = s < SLOTS && row[s];
          if (on && start === null) start = s;
          if (!on && start !== null) {
            ranges.push(`${this.slotLabel(start)} – ${s === SLOTS ? '24:00' : this.slotLabel(s)}`);
            start = null;
          }
        }
        return { day: this.dayLabels[d], ranges };
      });
    },
    statsRangeParam() {
      const [from, to] = this.statsRange || [];
      return this.statsCustom ? (from ? { from, to: to || from } : null) : { days: this.statsDays };
    },
    statsPeriodLabel() {
      if (!this.statsCustom) return `${this.statsDays} ngày`;
      const [from, to] = (this.statsRange || []).map(s => (s ? s.split('-').reverse().join('/') : s));
      return from === to ? `ngày ${from}` : `${from} – ${to}`;
    },
    bucketMax() {
      return this.stats ? Math.max(0, ...this.stats.buckets.map(b => b.count)) : 0;
    },
    referenceCoaches() {
      return this.coaches.filter(c => c.id !== this.selectedId && this.hasHours(c.id));
    },
    gridHasHours() {
      return this.grid.some(row => row.some(Boolean));
    },
    dirty() {
      return JSON.stringify(toStrings(this.grid)) !== this.savedSnapshot;
    },
  },
  mounted() {
    window.addEventListener('mouseup', this.endDrag);
  },
  beforeDestroy() {
    window.removeEventListener('mouseup', this.endDrag);
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        const [hoursRes, coachRes] = await Promise.all([
          decisionAiCoachService.getCoachActiveHours(),
          this.canManageAll ? decisionAiCoachService.getCoachesInCourse(ONBOARDING_ID) : Promise.resolve(null),
        ]);
        this.hoursMap = (hoursRes && hoursRes.data) || {};

        const me = this.currentUser || {};
        const myId = me.id || me.Id;
        this.coaches = this.canManageAll
          ? ((coachRes && coachRes.data) || []).map(c => ({ id: c.id, fullName: c.fullName }))
          : myId ? [{ id: myId, fullName: me.fullName || me.FullName || 'Tôi' }] : [];

        const first = this.coaches.find(c => c.id === myId) || this.coaches[0];
        this.applyCoach(first ? first.id : null);
      } catch (e) {
        console.error('[CoachActiveHours.load]', e);
        this.$toastr.e({ title: 'Lỗi', msg: 'Không tải được thời gian hoạt động' });
      } finally {
        this.loading = false;
      }
    },
    applyCoach(id) {
      this.selectedId = id;
      this.grid = fromStrings(id ? this.hoursMap[id] : null);
      this.savedSnapshot = JSON.stringify(toStrings(this.grid));
      this.referenceId = null;
      this.showReference = false;
      this.editing = !this.gridHasHours;
      this.loadStats(id);
    },
    async selectCoach(id) {
      if (id === this.selectedId) return;
      if (this.dirty) {
        const ok = await this.$bvModal.msgBoxConfirm('Khung giờ đang sửa chưa lưu. Bỏ thay đổi và chuyển coach?', {
          title: 'Chưa lưu',
          okTitle: 'Bỏ thay đổi',
          cancelTitle: 'Ở lại',
          centered: true,
        });
        if (!ok) return;
      }
      this.applyCoach(id);
    },
    hasHours(id) {
      const days = this.hoursMap[id];
      return Array.isArray(days) && days.some(s => (s || '').includes('1'));
    },
    initials(name) {
      return (name || '?').split(' ').filter(Boolean).slice(-2).map(n => n[0]).join('').toUpperCase();
    },
    slotLabel(s) {
      const m = (s % SLOTS) * 30;
      return `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    },
    // Kéo: tô (hoặc xoá nếu bắt đầu từ ô đã tô) cả vùng chữ nhật từ ô đầu tới ô đang trỏ
    startDrag(d, s) {
      if (!this.editing) return;
      this.drag = { d0: d, s0: s, d1: d, s1: s, value: !this.grid[d][s] };
    },
    moveDrag(d, s) {
      if (!this.drag) return;
      this.drag.d1 = d;
      this.drag.s1 = s;
    },
    inDrag(d, s) {
      const g = this.drag;
      return g && d >= Math.min(g.d0, g.d1) && d <= Math.max(g.d0, g.d1) && s >= Math.min(g.s0, g.s1) && s <= Math.max(g.s0, g.s1);
    },
    cellState(d, s) {
      return this.inDrag(d, s) ? this.drag.value : this.grid[d][s];
    },
    endDrag() {
      const g = this.drag;
      if (!g) return;
      for (let d = Math.min(g.d0, g.d1); d <= Math.max(g.d0, g.d1); d++) {
        for (let s = Math.min(g.s0, g.s1); s <= Math.max(g.s0, g.s1); s++) this.$set(this.grid[d], s, g.value);
      }
      this.drag = null;
    },
    toggleDay(d) {
      if (!this.editing) return;
      const value = !this.grid[d].every(Boolean);
      this.$set(this.grid, d, Array(SLOTS).fill(value));
    },
    toggleHour(h) {
      if (!this.editing) return;
      const slots = [h * 2, h * 2 + 1];
      const value = !this.grid.every(row => slots.every(s => row[s]));
      this.grid.forEach(row => slots.forEach(s => this.$set(row, s, value)));
    },
    async loadStats(coachId) {
      this.stats = null;
      // Tuỳ chọn mà chưa chọn ngày thì chưa tải
      if (!coachId || !this.statsTypes.length || !this.statsRangeParam) return;
      this.statsLoading = true;
      try {
        const body = (await decisionAiCoachService.getCoachResponseStats(coachId, this.statsRangeParam, this.statsTypes)) || {};
        // Đổi coach giữa chừng thì bỏ kết quả cũ
        if (coachId === this.selectedId) this.stats = body.success ? body.data : null;
      } catch (e) {
        console.error('[CoachActiveHours.loadStats]', e);
      } finally {
        if (coachId === this.selectedId) this.statsLoading = false;
      }
    },
    formatMinutes(m) {
      if (m == null) return '—';
      if (m < 60) return `${Math.round(m)} phút`;
      const h = Math.floor(m / 60);
      const rest = Math.round(m % 60);
      return rest ? `${h}g${rest}p` : `${h} giờ`;
    },
    applyReference() {
      const ref = this.coaches.find(c => c.id === this.referenceId);
      if (!ref) return;
      this.grid = fromStrings(this.hoursMap[ref.id]);
      this.$toastr.s({ title: 'Đã áp dụng', msg: `Lấy khung giờ của ${ref.fullName}, bấm Lưu để lưu` });
    },
    // Huỷ: coach đã có khung giờ thì trả lưới về bản đã lưu và khoá lại; chưa có thì đóng modal
    cancelEdit() {
      if (!this.hasHours(this.selectedId)) {
        this.$emit('change', false);
        return;
      }
      this.grid = fromStrings(this.hoursMap[this.selectedId]);
      this.referenceId = null;
      this.showReference = false;
      this.editing = false;
    },
    // Xoá hết ô = nhắc mọi lúc; vẫn phải bấm Lưu
    resetGrid() {
      this.grid = emptyGrid();
    },
    async saveFor(coachId, days) {
      const body = (await decisionAiCoachService.saveCoachActiveHours(coachId, days)) || {};
      if (!body.success) throw new Error(body.error || 'Lưu thất bại');
      if (days.some(s => s.includes('1'))) this.$set(this.hoursMap, coachId, days);
      else this.$delete(this.hoursMap, coachId);
    },
    async save() {
      if (!this.selectedId) return;
      this.saving = true;
      try {
        const days = toStrings(this.grid);
        await this.saveFor(this.selectedId, days);
        this.savedSnapshot = JSON.stringify(days);
        this.editing = !this.gridHasHours;
        this.showReference = false;
        this.$toastr.s({ title: 'Thành công', msg: 'Đã lưu thời gian hoạt động' });
      } catch (e) {
        console.error('[CoachActiveHours.save]', e);
        this.$toastr.e({ title: 'Lỗi', msg: e.message || 'Lưu thất bại, vui lòng thử lại' });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.cah {
  display: flex;
  gap: 16px;
  min-height: 380px;
}

.cah-coaches {
  width: 220px;
  flex-shrink: 0;
  max-height: 520px;
  overflow-y: auto;
  border-right: 1px solid #eee;
  padding-right: 12px;
}

.cah-empty {
  padding: 12px 4px;
  font-size: 12px;
  color: #999;
}

.cah-coach-list {
  margin-bottom: 0;
}

.cah-coach {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border: none;
  border-radius: 6px !important;
  font-size: 13px;

  &.active {
    background: #E1F5EE;
    color: #0F6E56;
    font-weight: 600;
  }

  &__avatar {
    flex-shrink: 0;
    background: #E1F5EE;
    color: #0F6E56;
    font-size: 11px;
    font-weight: 600;
  }

  &__name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__dot {
    width: 8px;
    height: 8px;
    flex-shrink: 0;
    border-radius: 50%;
    background: #1D9E75;
  }
}

.cah-main {
  flex: 1;
  min-width: 0;

  &__head {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 15px;
  }
}

.cah-bottom {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-top: 12px;
  align-items: start;
}

.cah-stats {
  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }

  &__title {
    display: inline-flex;
    align-items: center;
    gap: 4px;
  }

  &__info {
    display: inline-flex;
    color: #999;
    cursor: help;

    &:hover { color: #0F6E56; }
  }

  &__types {
    margin: 4px 0 8px;
    font-size: 12px;
  }

  &__range {
    width: 100%;
    margin-bottom: 6px;
  }

  &__days ::v-deep .btn {
    height: 26px;
    padding: 0 10px;
    font-size: 12px;
  }

  &__tiles {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 6px;
    margin-bottom: 10px;
  }

  &__tile {
    display: flex;
    flex-direction: column;
    padding: 6px 8px;
    border-radius: 6px;
    background: #F4FBF8;

    b {
      font-size: 15px;
      color: #0F6E56;
    }

    span {
      font-size: 11px;
      color: #777;
    }

    &.warn b { color: #BA7517; }
  }

  &__bucket {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 2px 0;
  }

  &__label {
    width: 72px;
    flex-shrink: 0;
    color: #555;
  }

  &__track {
    flex: 1;
    height: 10px;
    border-radius: 5px;
    background: #F1F2F4;
    overflow: hidden;
  }

  &__bar {
    display: block;
    height: 100%;
    border-radius: 5px;
    background: #1D9E75;
  }

  &__count {
    width: 28px;
    flex-shrink: 0;
    text-align: right;
    color: #555;
  }
}

.cah-summary {
  padding: 10px 12px;
  border: 1px solid #eee;
  border-radius: 6px;
  font-size: 12px;

  &__title {
    margin-bottom: 6px;
    font-weight: 600;
    color: #444;
  }

  &__empty,
  &__off {
    color: #999;
  }

  &__row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 2px 0;
  }

  &__day {
    width: 28px;
    flex-shrink: 0;
    font-weight: 500;
    color: #555;
  }

  &__ranges {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
  }

  &__range {
    border: 1px solid #C8DCF0;
    background: #EEF5FC;
    color: #185FA5;
    font-weight: 500;
  }
}

.cah-desc {
  margin: 6px 0 10px;
  font-size: 12px;
  color: #777;
}

.cah-grid-wrap {
  overflow-x: auto;
  user-select: none;
}

.cah-grid {
  border-collapse: collapse;
  width: 100%;
  table-layout: fixed;

  &__corner { width: 36px; }

  &__hour {
    padding: 0 0 4px;
    font-size: 11px;
    font-weight: 500;
    color: #777;
    text-align: left;
    cursor: pointer;
    white-space: nowrap;
  }

  &__day {
    padding-right: 6px;
    font-size: 12px;
    font-weight: 500;
    color: #555;
    cursor: pointer;
  }

  &__cell {
    height: 30px;
    border-top: 1px solid #e6e6e6;
    border-bottom: 1px solid #e6e6e6;
    border-left: 1px solid #e6e6e6;
    background: #fff;
    cursor: pointer;

    // Viền đậm ở mốc giờ, nhạt ở mốc nửa giờ
    &.half { border-left-color: #f3f3f3; }
    &.on { background: #85B7EB; }
    &:hover { outline: 1px solid #378ADD; outline-offset: -1px; }
  }

  // Đang khoá: chỉ xem, không kéo thả / bấm nhãn
  &.locked {
    .cah-grid__cell,
    .cah-grid__day,
    .cah-grid__hour {
      cursor: default;
    }

    .cah-grid__cell:hover { outline: none; }
    .cah-grid__cell.on { background: #A9CBEF; }
  }
}

.cah-ref {
  max-width: 420px;
}

.cah-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 16px;
}
</style>
